import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { IonModal, ModalController, Platform } from '@ionic/angular';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ResultDrawerViewModel, ScoreStatus } from './result-drawer-view-model';
import { RoomDisplayResult } from 'src/app/shared/types/room-display-result';
import { launchConfetti } from '../../game-result/confetti';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { ProfileService } from 'src/app/core/services/profile/profile.service';
import { UxGameService } from 'src/app/core/services/ux-game/ux-game.service';
import { safeModalDismiss } from 'src/app/shared/utils/modal.utils';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';

@Component({
  selector: 'app-result-drawer',
  templateUrl: './result-drawer.component.html',
  styleUrls: ['./result-drawer.component.scss'],
})
export class ResultDrawerComponent implements OnInit {
  @Input({ required: true }) viewModel!: ResultDrawerViewModel;
  @ViewChild(IonModal) modal!: IonModal;
  @Input() isAfterGame = false;
  @Input() isFreeGame = false;
  @Input() worldWide = false;

  private themeService = inject(ThemeService);
  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private profileService = inject(ProfileService);
  private uxGameService = inject(UxGameService);
  private analyticsService = inject(AnalyticsService);
  OperatorService = OperatorService;
  ScoreStatus = ScoreStatus;

  drawerConfig = this.themeService.drawerConfig();
  currencyIcon?: string;
  currentUserId!: string;
  statsUserId!: string;

  private readonly resultEventMap: Record<RoomDisplayResult, EventName> = {
    [RoomDisplayResult.Win]: EventName.ResultDrawerWinViewed,
    [RoomDisplayResult.Lose]: EventName.ResultDrawerLossViewed,
    [RoomDisplayResult.Draw]: EventName.ResultDrawerDrawViewed,
    [RoomDisplayResult.Cancelled]: EventName.ResultDrawerCancelledViewed,
    [RoomDisplayResult.InProgress]: EventName.ResultDrawerInProgressViewed,
  };

  private logResultEvent(result: RoomDisplayResult): void {
    const eventName = this.resultEventMap[result];
    if (eventName) {
      this.analyticsService.log(eventName);
    } else {
      this.analyticsService.errorWarning(`RoomDisplayResult not implemented: ${result}`);
    }
  }

  async ngOnInit(): Promise<void> {
    this.currentUserId = this.profileService.id;
    this.currencyIcon = this.themeService.currencyIcon();
    this.isFreeGame = this.viewModel.isFree;

    if (this.viewModel.roomResult === RoomDisplayResult.Win) {
      launchConfetti(this.themeService);
    }

    this.logResultEvent(this.viewModel.roomResult);
  }

  get glowCss(): string {
    return this.viewModel.getGlowCss(this.themeService.drawerConfig().gradient === 'circle');
  }

  get currencyAmount(): string | undefined {
    return this.viewModel.prize;
  }

  get wonBountyAmount(): number | undefined {
    if (this.viewModel.wonBountyAmount !== undefined) {
      return this.viewModel.wonBountyAmount / 100;
    }
    return undefined;
  }

  async canDismiss(data?: unknown, role?: string) {
    return role !== 'gesture';
  }

  async onCloseClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
  }

  async onFinishClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
    this.uxGameService.resetToLoading();
  }

  onPlayerClick(userId: string | null | undefined) {
    if (userId) {
      this.statsUserId = userId;
      this.modal.present();
    }
  }

  isMobile(): boolean {
    return this.platform.is('mobile');
  }
}
